/**
 * shared-delivery-utils.js — Gemeinsame Liefer-/Versand-Logik
 *
 * Einzige Wahrheit fuer: Europe/Berlin-Zeit, DE-Feiertage, Shipping-Profile,
 * Werktags-/Lieferdatum-Berechnung. Genutzt von:
 *   - custom-express-shipping.js (Express-Badge-Sichtbarkeit)
 *   - custom-pdp-injector.js     (Lieferdatum-Schaetzung auf der PDP)
 *
 * Dual-Mode: laeuft als Browser-<script> (window.DeliveryUtils) UND als Node-Modul
 * (module.exports) -> identische Logik wird in tests/delivery-utils.test.js getestet.
 *
 * Datums-Arithmetik ist TZ-sicher ueber Date.UTC (keine DST-Drift). Der einzige
 * Eintrittspunkt fuer "echte" Zeit ist getBerlinNow(); reine Funktionen bekommen
 * `now`/`holidays` injiziert und sind damit deterministisch testbar.
 */
(function (root, factory) {
  'use strict';
  var API = factory();
  if (typeof module !== 'undefined' && module.exports) { module.exports = API; } // Node / Tests
  if (root) { root.DeliveryUtils = API; }                                        // Browser
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  // Bundesweite DE-Feiertage 2026-2028 (YYYY-MM-DD). Bei jaehrlicher Pflege: 2029+ ergaenzen.
  var GERMAN_HOLIDAYS = new Set([
    // 2026
    '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-01', '2026-05-14',
    '2026-05-25', '2026-10-03', '2026-12-24', '2026-12-25', '2026-12-26', '2026-12-31',
    // 2027
    '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-01', '2027-05-06',
    '2027-05-17', '2027-10-03', '2027-12-24', '2027-12-25', '2027-12-26', '2027-12-31',
    // 2028
    '2028-01-01', '2028-04-14', '2028-04-17', '2028-05-01', '2028-05-25',
    '2028-06-05', '2028-10-03', '2028-12-24', '2028-12-25', '2028-12-26', '2028-12-31',
  ]);

  // Shipping-Profile: Cutoff + Lieferzeit (Werktage NACH Versand).
  // express businessDays:1 = "Versand naechster Werktag + 1 WT Zustellung".
  var SHIPPING_PROFILES = {
    express: { cutoffHour: 12, businessDays: 1, leadTimeLabel: 'ca. 1-2 Werktage' }, // Paket-Versand
    palette: { cutoffHour: 14, businessDays: 5, leadTimeLabel: 'ca. 3-5 Werktage' }, // Spedition (Dach-Systeme etc.)
  };

  /**
   * Aktuelle Zeit in Europe/Berlin (CET/CEST automatisch via Intl).
   * @returns {{isoDate:string, hour:number, minute:number, weekday:number}} weekday 1..7 (Mo=1..So=7)
   */
  function getBerlinNow() {
    var fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Berlin',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false, weekday: 'short',
    });
    var parts = fmt.formatToParts(new Date());
    var p = {};
    parts.forEach(function (x) { p[x.type] = x.value; });
    var weekdayMap = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
    return {
      isoDate: p.year + '-' + p.month + '-' + p.day,
      hour: parseInt(p.hour, 10),
      minute: parseInt(p.minute, 10),
      weekday: weekdayMap[p.weekday],
    };
  }

  /**
   * Shipping-Profil-Auswahl. Reihenfolge:
   *  1. Tag 'palette' / 'express' (expliziter Override gewinnt)
   *  2. Handle in expressHandles (Metafeld custom.express_shipping — gleiche Quelle wie Badge)
   *  3. Legacy: type === 'Auffanggurt'
   *  4. Fallback: palette
   */
  function selectProfile(ctx, expressHandles) {
    ctx = ctx || {};
    var tags = Array.isArray(ctx.tags) ? ctx.tags.map(function (t) { return String(t).toLowerCase(); }) : [];
    var handles = Array.isArray(expressHandles) ? expressHandles : [];
    if (tags.indexOf('palette') !== -1) return SHIPPING_PROFILES.palette;
    if (tags.indexOf('express') !== -1) return SHIPPING_PROFILES.express;
    if (ctx.handle && handles.indexOf(ctx.handle) !== -1) return SHIPPING_PROFILES.express;
    if (ctx.type === 'Auffanggurt') return SHIPPING_PROFILES.express;
    return SHIPPING_PROFILES.palette;
  }

  // --- TZ-sichere Kalendertag-Arithmetik (UTC-Mitternacht als Anker) ---
  function isoToUTCDate(iso) {
    var p = iso.split('-');
    return new Date(Date.UTC(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10)));
  }
  function utcDateToIso(dt) {
    var mm = String(dt.getUTCMonth() + 1);
    var dd = String(dt.getUTCDate());
    if (mm.length < 2) mm = '0' + mm;
    if (dd.length < 2) dd = '0' + dd;
    return dt.getUTCFullYear() + '-' + mm + '-' + dd;
  }
  function addDaysIso(iso, n) {
    var dt = isoToUTCDate(iso);
    dt.setUTCDate(dt.getUTCDate() + n);
    return utcDateToIso(dt);
  }
  function isWeekendIso(iso) {
    var wd = isoToUTCDate(iso).getUTCDay(); // 0=So .. 6=Sa
    return wd === 0 || wd === 6;
  }
  function isHolidayIso(iso, holidays) {
    return !!(holidays && holidays.has(iso));
  }
  function isBusinessDayIso(iso, holidays) {
    return !isWeekendIso(iso) && !isHolidayIso(iso, holidays);
  }
  function nextBusinessDayIso(iso, holidays) {
    var d = addDaysIso(iso, 1);
    while (!isBusinessDayIso(d, holidays)) d = addDaysIso(d, 1);
    return d;
  }
  function addBusinessDaysIso(iso, count, holidays) {
    var d = iso, added = 0;
    while (added < count) {
      d = addDaysIso(d, 1);
      if (isBusinessDayIso(d, holidays)) added++;
    }
    return d;
  }

  var DE_WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  function weekdayNameDe(iso) {
    return DE_WEEKDAYS[isoToUTCDate(iso).getUTCDay()];
  }
  function formatDeliveryDateIso(iso) {
    var dt = isoToUTCDate(iso);
    var dd = String(dt.getUTCDate());
    var mm = String(dt.getUTCMonth() + 1);
    if (dd.length < 2) dd = '0' + dd;
    if (mm.length < 2) mm = '0' + mm;
    return weekdayNameDe(iso) + ', ' + dd + '.' + mm + '.';
  }

  /**
   * Kernberechnung. `now` = Ergebnis von getBerlinNow() (injizierbar fuer Tests).
   * Versand heute nur an Werktag (kein Sa/So/Feiertag) vor Cutoff.
   * @returns {{shipsToday:boolean, remainingMin:number, shipStartIso:string,
   *            deliveryIso:string, nextCutoff:({isoDate:string,hour:number}|null)}}
   */
  function computeDelivery(profile, now, holidays) {
    var weekendToday = now.weekday >= 6;
    var holidayToday = isHolidayIso(now.isoDate, holidays);
    var beforeCutoff = now.hour < profile.cutoffHour;
    var shipsToday = !weekendToday && !holidayToday && beforeCutoff;

    var shipStartIso, nextCutoff, remainingMin;
    if (shipsToday) {
      shipStartIso = now.isoDate;
      nextCutoff = null;
      remainingMin = (profile.cutoffHour - now.hour) * 60 - (now.minute || 0);
      if (remainingMin < 0) remainingMin = 0;
    } else {
      shipStartIso = nextBusinessDayIso(now.isoDate, holidays);
      nextCutoff = { isoDate: shipStartIso, hour: profile.cutoffHour };
      remainingMin = 0;
    }
    var deliveryIso = addBusinessDaysIso(shipStartIso, profile.businessDays, holidays);
    return {
      shipsToday: shipsToday,
      remainingMin: remainingMin,
      shipStartIso: shipStartIso,
      deliveryIso: deliveryIso,
      nextCutoff: nextCutoff,
      leadTime: profile.leadTimeLabel,
    };
  }

  /**
   * Baut den inneren HTML-String der Lieferzeit-Zeile.
   * @param info Rueckgabe von computeDelivery()
   * @param mode 'legacy' (Default, festes "Lieferung bis <Datum>") | 'ca' (rechtlich entschaerft:
   *        "Lieferung ca. <Datum>" + bestimmbare Obergrenze "· Lieferzeit ca. X Werktage")
   */
  function composeDeliveryText(info, mode) {
    mode = mode === 'ca' ? 'ca' : 'legacy';
    var dateStr = formatDeliveryDateIso(info.deliveryIso);
    var lieferPrefix = mode === 'ca' ? 'Lieferung ca. ' : 'Lieferung bis ';
    var leadSuffix = (mode === 'ca' && info.leadTime) ? ', Lieferzeit ' + info.leadTime : '';
    var dateHtml = '<strong class="custom-delivery-estimate__date">' + dateStr + '</strong>';

    if (info.shipsToday) {
      var totalMin = info.remainingMin;
      var h = Math.floor(totalMin / 60);
      var m = totalMin % 60;
      var countdownStr;
      if (h >= 1) countdownStr = h + 'h ' + String(m).padStart(2, '0') + 'min';
      else if (m >= 1) countdownStr = m + ' Minuten';
      else countdownStr = 'unter 1 Min';
      return 'Bestelle in <strong class="custom-delivery-estimate__countdown">'
        + countdownStr + '</strong> &rarr; ' + lieferPrefix + dateHtml + leadSuffix;
    }
    if (info.nextCutoff) {
      return 'Bestelle bis <strong class="custom-delivery-estimate__countdown">'
        + weekdayNameDe(info.nextCutoff.isoDate) + ' ' + info.nextCutoff.hour + ':00 Uhr</strong> &rarr; '
        + lieferPrefix + dateHtml + leadSuffix;
    }
    return 'Naechster Versand &rarr; ' + lieferPrefix + dateHtml + leadSuffix;
  }

  return {
    GERMAN_HOLIDAYS: GERMAN_HOLIDAYS,
    SHIPPING_PROFILES: SHIPPING_PROFILES,
    getBerlinNow: getBerlinNow,
    selectProfile: selectProfile,
    addDaysIso: addDaysIso,
    isWeekendIso: isWeekendIso,
    isHolidayIso: isHolidayIso,
    isBusinessDayIso: isBusinessDayIso,
    nextBusinessDayIso: nextBusinessDayIso,
    addBusinessDaysIso: addBusinessDaysIso,
    weekdayNameDe: weekdayNameDe,
    formatDeliveryDateIso: formatDeliveryDateIso,
    computeDelivery: computeDelivery,
    composeDeliveryText: composeDeliveryText,
  };
});
